// One line above and one below the code, printed in the file (#305). Free on
// every plan. The server draws Plus Jakarta Sans outlines for letters, numbers
// and common punctuation; anything else (an emoji, say) comes back as a
// message under its field, through `errors`.
export const TEXT_MAX_LENGTH = 20

const WEIGHTS = [
  { value: "light", label: "Light" },
  { value: "bold",  label: "Bold" }
]

function TextField({ id, label, placeholder, value, error, large, onChange }) {
  const errorId = `${id}-error`
  return (
    <div>
      <label htmlFor={id} className="flex items-baseline justify-between gap-2 text-xs font-semibold mb-1">
        <span>{label}</span>
        <span className="font-mono font-normal text-[color:var(--color-muted)] dark:text-muted-light" aria-hidden="true">
          {value.length}/{TEXT_MAX_LENGTH}
        </span>
      </label>
      <input
        id={id}
        type="text"
        value={value}
        maxLength={TEXT_MAX_LENGTH}
        placeholder={placeholder}
        autoComplete="off"
        aria-invalid={error ? "true" : undefined}
        aria-describedby={error ? errorId : undefined}
        onChange={(e) => onChange(e.target.value)}
        className={
          "w-full border rounded px-3 py-2 bg-transparent " + (large ? "text-base " : "text-sm ") + " focus:outline-none focus:ring-2 focus:ring-voltage focus:ring-offset-1 focus:border-voltage " +
          (error ? "border-[color:var(--color-fail)]" : "border-[color:var(--color-border)] dark:border-storm")
        }
      />
      {error && (
        <p id={errorId} className="mt-1 text-xs text-[color:var(--color-fail)]">{error}</p>
      )}
    </div>
  )
}

// `name` must be unique per instance: the dashboard can mount the creator's
// panel and the edit modal at once, and same-named radios form one group.
export function TextWeightSelect({ value, onChange, name = "text_weight" }) {
  const current = value || "bold"
  return (
    <fieldset>
      <legend className="sr-only">Text weight</legend>
      <div className="grid grid-cols-2 gap-2">
        {WEIGHTS.map((w) => {
          const selected = current === w.value
          return (
            <label
              key={w.value}
              className={
                "flex items-center justify-center py-1.5 rounded border cursor-pointer transition text-xs has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-voltage has-[:focus-visible]:ring-offset-1 " +
                (w.value === "bold" ? "font-bold " : "font-light ") +
                (selected
                  ? "border-voltage bg-voltage/5"
                  : "border-[color:var(--color-border)] dark:border-storm hover:border-voltage")
              }
            >
              <input
                type="radio"
                name={name}
                value={w.value}
                checked={selected}
                onChange={() => onChange(w.value)}
                className="sr-only"
              />
              {w.label}
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}

// `idPrefix` keeps the ids unique where two builders share a page. `columns`
// puts the two fields side by side (the dashboard's wide panel). `large` sets
// 16px text, so iOS doesn't zoom the page when a field takes focus.
export default function TextFields({ design, setDesign, idPrefix = "qr-text", columns = false, large = false }) {
  const errors = design.textErrors || {}
  const hasText = Boolean((design.headerText || "").trim() || (design.footerText || "").trim())
  // Typing clears that field's message; the next save checks it again.
  const update = (key) => (value) =>
    setDesign((d) => ({ ...d, [key]: value, textErrors: d.textErrors ? { ...d.textErrors, [key]: null } : null }))

  return (
    <div className="space-y-3">
      <div className={columns ? "grid grid-cols-1 sm:grid-cols-2 gap-3" : "space-y-3"}>
      <TextField
        id={`${idPrefix}-header`}
        label="Text above the code"
        placeholder="Your business name"
        value={design.headerText || ""}
        error={errors.headerText}
        large={large}
        onChange={update("headerText")}
      />
      <TextField
        id={`${idPrefix}-footer`}
        label="Text below the code"
        placeholder="Scan me"
        value={design.footerText || ""}
        error={errors.footerText}
        large={large}
        onChange={update("footerText")}
      />
      </div>
      {hasText && (
        <TextWeightSelect
          name={`${idPrefix}-weight`}
          value={design.textWeight}
          onChange={(textWeight) => setDesign((d) => ({ ...d, textWeight }))}
        />
      )}
      <p className="text-xs text-[color:var(--color-muted)] dark:text-muted-light">
        Printed in the file. No editor needed.
      </p>
    </div>
  )
}
